import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, Calendar, MapPin, Ticket, Users, Plane, Hotel, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import Footer from "../components/Footer";
import InstagramShareCard from "../components/InstagramShareCard";
import { haptic } from "../utils/haptic";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sharp QR Code generator (SVG-based with crisp edges)
const QRCode = ({ value, size = 160 }) => {
  // Generate a deterministic pattern based on value
  const hash = value.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  
  const grid = [];
  const gridSize = 25; // Larger grid for sharper QR
  const cellSize = size / gridSize;
  
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      // Position patterns (corners) - standard QR finder patterns
      const isTopLeft = (i < 7 && j < 7);
      const isTopRight = (i < 7 && j >= gridSize - 7);
      const isBottomLeft = (i >= gridSize - 7 && j < 7);
      const isCorner = isTopLeft || isTopRight || isBottomLeft;
      
      // Finder pattern logic
      const inFinderOuter = isCorner && (
        i === 0 || i === 6 || j === 0 || j === 6 ||
        (isTopRight && (j === gridSize - 7 || j === gridSize - 1)) ||
        (isBottomLeft && (i === gridSize - 7 || i === gridSize - 1))
      );
      const inFinderInner = isCorner && (
        (i >= 2 && i <= 4 && j >= 2 && j <= 4) ||
        (isTopRight && i >= 2 && i <= 4 && j >= gridSize - 5 && j <= gridSize - 3) ||
        (isBottomLeft && i >= gridSize - 5 && i <= gridSize - 3 && j >= 2 && j <= 4)
      );
      
      // Timing patterns
      const isTiming = (i === 6 && j > 7 && j < gridSize - 8 && j % 2 === 0) ||
                       (j === 6 && i > 7 && i < gridSize - 8 && i % 2 === 0);
      
      // Data pattern - more density for authentic look
      const dataHash = Math.abs((hash * (i + 1) * (j + 1)) % 100);
      const isData = !isCorner && !isTiming && dataHash < 42;
      
      if (inFinderOuter || inFinderInner || isTiming || isData) {
        grid.push({ x: j, y: i });
      }
    }
  }
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`} 
      className="rounded-lg"
      style={{ imageRendering: 'crisp-edges' }}
    >
      <rect width={size} height={size} fill="white" rx="6" />
      {grid.map((cell, index) => (
        <rect
          key={index}
          x={cell.x * cellSize}
          y={cell.y * cellSize}
          width={cellSize}
          height={cellSize}
          fill="#000000"
        />
      ))}
    </svg>
  );
};

const DigitalTicket = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const sessionId = searchParams.get("session_id");
  const tier = searchParams.get("tier") || "ticketOnly";

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/checkout/session/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          setBooking(data);
        }
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingDetails();
  }, [sessionId]);

  const tierLabels = {
    ticketOnly: "Event Access",
    ticketFlight: "Event + Flight",
    completeTrip: "Complete Experience"
  };

  if (loading) {
    return (
      <div className="min-h-screen animated-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
            <Ticket className="w-8 h-8 text-primary" />
          </div>
          <p className="font-mono text-muted-foreground">Loading your ticket...</p>
        </div>
      </div>
    );
  }

  const bookingId = sessionId?.slice(-8).toUpperCase() || "XTRIP001";
  const eventName = booking?.metadata?.event_name || "Premium Event Experience";
  const guests = booking?.metadata?.guests || "1";

  return (
    <div className="min-h-screen animated-bg pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4">
        {/* Success Header - Operational Transparency */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 border-2 border-accent flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-accent" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-foreground mb-3">
            You're In
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
            Our agents are now manually verifying your flight slots to ensure zero-friction boarding. 
            <span className="block mt-1 text-primary/80 italic text-sm">We sweat the small stuff so you don't have to.</span>
          </p>
        </div>
        
        {/* Operational Status */}
        <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
            <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            <span className="text-accent font-medium">LIVE:</span> Synchronizing with airline manifests • ETA confirmation in 4 minutes
          </div>
        </div>

        {/* Digital Ticket Card */}
        <div className="glass-strong rounded-2xl overflow-hidden mb-8">
          {/* Ticket Header */}
          <div className="bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 p-6 border-b border-border/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary flex items-center justify-center rounded-lg">
                  <Ticket className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground">XTRIP</div>
                  <div className="text-xs text-muted-foreground font-mono">DIGITAL PASS</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground uppercase">Booking ID</div>
                <div className="font-mono text-lg font-bold text-primary">{bookingId}</div>
              </div>
            </div>
          </div>

          {/* Ticket Body */}
          <div className="p-6">
            {/* QR Code and Details */}
            <div className="flex flex-col sm:flex-row gap-6 mb-6">
              {/* QR Code */}
              <div className="flex-shrink-0 mx-auto sm:mx-0">
                <div className="p-4 bg-white rounded-xl shadow-lg">
                  <QRCode value={sessionId || "XTRIP-DEMO"} size={160} />
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3 font-mono uppercase tracking-wider">
                  Scan at entry
                </p>
              </div>

              {/* Event Details */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Experience</div>
                  <div className="font-heading text-xl font-bold text-foreground">{eventName}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Package</div>
                    <div className="font-mono text-sm text-foreground">{tierLabels[tier]}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Guests</div>
                    <div className="font-mono text-sm text-foreground flex items-center gap-1">
                      <Users className="w-4 h-4 text-primary" />
                      {guests}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Included Items */}
            <div className="border-t border-border/50 pt-6">
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Included in Your Package</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="glass rounded-lg p-3 text-center">
                  <Ticket className="w-5 h-5 text-primary mx-auto mb-1" />
                  <div className="text-xs text-foreground font-medium">Event</div>
                  <div className="text-xs text-accent">✓ Included</div>
                </div>
                <div className={`glass rounded-lg p-3 text-center ${tier === "ticketOnly" ? "opacity-40" : ""}`}>
                  <Plane className="w-5 h-5 text-secondary mx-auto mb-1" />
                  <div className="text-xs text-foreground font-medium">Flight</div>
                  <div className="text-xs text-accent">{tier !== "ticketOnly" ? "✓ Included" : "—"}</div>
                </div>
                <div className={`glass rounded-lg p-3 text-center ${tier !== "completeTrip" ? "opacity-40" : ""}`}>
                  <Hotel className="w-5 h-5 text-accent mx-auto mb-1" />
                  <div className="text-xs text-foreground font-medium">Hotel</div>
                  <div className="text-xs text-accent">{tier === "completeTrip" ? "✓ Included" : "—"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Footer */}
          <div className="bg-muted/30 px-6 py-4 border-t border-border/50">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="font-mono">Powered by XTrip</span>
              <span className="font-mono">128BPM Travel Engine</span>
            </div>
          </div>
        </div>

        {/* Instagram Stories Share Card */}
        <div className="mb-8">
          <InstagramShareCard
            booking={booking}
            userName={booking?.customer_email?.split('@')[0] || "Music Lover"}
            trackTitle="Progressive House Loop"
            eventName={eventName}
            eventCity={booking?.metadata?.origin || "London"}
            eventDate={new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            qrData={`https://electronic-hub-15.preview.emergentagent.com/booking/${bookingId}`}
            bookingId={bookingId}
          />
        </div>

        {/* Browse More */}
        <div className="mb-8">
          <Link to="/events">
            <Button className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
              Browse More Experiences
            </Button>
          </Link>
        </div>

        {/* Help Text */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Confirmation email sent. Questions? Contact{" "}
            <a href="mailto:hello@xtriptour.com" className="text-primary hover:underline">
              hello@xtriptour.com
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DigitalTicket;